import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  completeQetafSubscription,
  emptyQetaf,
  redeemQetafPoints,
  requestQetafOtp,
} from "../../../Actions/QetafAction";
import { addPaymentDataWithCode } from "../../../Actions/MobilePaymentActions";
import { emptyStatusMadaId, prepareCheckout } from "../../../Actions/HyperPayAction";
import {
  PORTAL_CREDIT_CARD,
  PORTAL_LAND_PAGE_SUBSCRIBE_NOW,
  PORTAL_MADA,
  PORTAL_SUCCESSFUL,
} from "../../../routes";
import qitafLogo from "../../../images/portal/icons/qitaf.svg";
import mainLogo from "../../../images/portal/icons/logo.svg";
import cardsLogo from "../../../images/portal/visa-mastercard-icon.svg";
import madaLogo from "../../../images/portal/mada.png";
import { JeelMADA, JeelQetaf } from "../../src/constants";

const getPackageType = (type) => {
  switch (type) {
    case "Monthly":
      return "الباقة الشهرية";
    case "Yearly":
      return "الباقة الاحترافية";
    case "Weekly":
      return "الباقة الأسبوعية";
    default:
      return "الباقة المختارة";
  }
};

const getCurrency = (payingData) => {
  const paymentPackage = payingData && payingData.paymentCurrencyPackage;
  if (!paymentPackage) {
    return "رس";
  }

  const keys = [
    "yearDiscountPriceConverted",
    "monthDiscountPriceConverted",
    "weekDiscountPriceConverted",
  ];
  const converted = keys
    .map((key) => paymentPackage[key])
    .find((item) => item && item.currency);

  return converted ? converted.currency : "رس";
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const getRemainingAmountUsd = (payingData, remainingAmount) => {
  if (!payingData || !payingData.paymentCurrencyPackage) {
    return Number(Number(remainingAmount || 0).toFixed(2));
  }

  const paymentPackage = payingData.paymentCurrencyPackage;
  let packageAmountUsd = 0;
  let packageAmountSar = 0;

  switch (payingData.paymentType) {
    case "Monthly":
      packageAmountUsd = Number(paymentPackage.monthDiscountPrice || 0);
      packageAmountSar = Number(
        (paymentPackage.monthDiscountPriceConverted &&
          paymentPackage.monthDiscountPriceConverted.convertedPrice) || 0
      );
      break;
    case "Yearly":
      packageAmountUsd = Number(paymentPackage.yearDiscountPrice || 0);
      packageAmountSar = Number(
        (paymentPackage.yearDiscountPriceConverted &&
          paymentPackage.yearDiscountPriceConverted.convertedPrice) || 0
      );
      break;
    case "Weekly":
      packageAmountUsd = Number(paymentPackage.weekDiscountPrice || 0);
      packageAmountSar = Number(
        (paymentPackage.weekDiscountPriceConverted &&
          paymentPackage.weekDiscountPriceConverted.convertedPrice) || 0
      );
      break;
    default:
      return Number(Number(remainingAmount || 0).toFixed(2));
  }

  if (remainingAmount <= 0) {
    return 0;
  }

  if (packageAmountSar > 0 && packageAmountUsd > 0) {
    return Number(((remainingAmount * packageAmountUsd) / packageAmountSar).toFixed(2));
  }

  return Number(Number(remainingAmount || 0).toFixed(2));
};

/** Display-only mask matching reference: +966 5X XXX X763 */
const formatMaskedMsisdnHint = (digits) => {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length >= 9) {
    return `+966 ${d[0]}X XXX X${d.slice(-3)}`;
  }
  if (d.length >= 1) {
    return `+966${d}`;
  }
  return "";
};

const normalizeSaudiMsisdn = (value) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (/^05\d{8}$/.test(digits)) {
    return digits.substring(1);
  }
  if (/^5\d{8}$/.test(digits)) {
    return digits;
  }
  if (/^9665\d{8}$/.test(digits)) {
    return digits.substring(3);
  }

  return "";
};

const getErrorMessage = (payload) => {
  if (!payload) {
    return "";
  }
  if (typeof payload === "string") {
    return payload;
  }
  return payload.message || payload.description || payload.code || "حدث خطأ ما، يرجى المحاولة لاحقاً";
};

const getQetafResponseMessage = (payload) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  return payload.message || payload.description || payload.code || "";
};

function QetafPayment() {
  const dispatch = useDispatch();
  const history = useHistory();
  const payingData = useSelector((state) => state.mobilePaymentPackage.payingData);
  const qetaf = useSelector((state) => state.qetaf);
  const hyperPay = useSelector((state) => state.hyperPay);
  const [phone, setPhone] = useState("");
  const [redeemAmount, setRedeemAmount] = useState("");
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const [formError, setFormError] = useState("");
  const [internalReference, setInternalReference] = useState("");
  const [pendingRedeemAmount, setPendingRedeemAmount] = useState(0);
  const [redeemedAmount, setRedeemedAmount] = useState(0);
  /** credit | mada — used when remaining balance > 0 (next-phase card capture). */
  const [remainingBalanceMethod, setRemainingBalanceMethod] = useState("credit");
  const [waitingMadaCheckout, setWaitingMadaCheckout] = useState(false);
  const pinRefs = useRef([]);
  const lastRedeemPayloadRef = useRef("");

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(emptyQetaf());
    dispatch(emptyStatusMadaId());

    return () => {
      dispatch(emptyQetaf());
      dispatch(emptyStatusMadaId());
    };
  }, [dispatch]);

  const totalAmount = Number(payingData && payingData.cash ? payingData.cash : 0);
  const currency = getCurrency(payingData);

  const normalizedRedeemAmount = useMemo(() => {
    const amount = parseInt(redeemAmount, 10);
    if (Number.isNaN(amount) || amount < 0) {
      return 0;
    }
    return amount;
  }, [redeemAmount]);

  const appliedQetafDiscount = redeemedAmount;
  const selectedQetafDiscount = normalizedRedeemAmount;
  const remainingAmount = Math.max(totalAmount - appliedQetafDiscount, 0);
  const msisdn = normalizeSaudiMsisdn(phone);
  const pin = pinDigits.join("");
  const otpMessage = getErrorMessage(qetaf.otpError);
  const redeemErrorMessage = getErrorMessage(qetaf.redeemError);
  const completeErrorMessage = getErrorMessage(qetaf.completeError);
  const redeemResponseMessage = getQetafResponseMessage(qetaf.redeemResponse);
  const redeemResponseOk =
    qetaf.redeemSuccess &&
    (!qetaf.redeemResponse ||
      !qetaf.redeemResponse.code ||
      String(qetaf.redeemResponse.code) === "0");

  const otpMsisdnHint = msisdn
    ? formatMaskedMsisdnHint(msisdn)
    : "";

  useEffect(() => {
    if (redeemResponseOk && pendingRedeemAmount) {
      setRedeemedAmount(pendingRedeemAmount);
    }
  }, [redeemResponseOk, pendingRedeemAmount]);

  useEffect(() => {
    if (qetaf.redeemError || (qetaf.redeemSuccess && !redeemResponseOk)) {
      lastRedeemPayloadRef.current = "";
    }
  }, [qetaf.redeemError, qetaf.redeemSuccess, redeemResponseOk]);

  useEffect(() => {
    if (qetaf.completeSuccess) {
      history.push(PORTAL_SUCCESSFUL + `?name=${JeelQetaf}`);
    }
  }, [qetaf.completeSuccess, history]);

  useEffect(() => {
    if (waitingMadaCheckout && hyperPay.idSuccess) {
      setWaitingMadaCheckout(false);
      history.push(PORTAL_MADA);
    }
  }, [waitingMadaCheckout, hyperPay.idSuccess, history]);

  useEffect(() => {
    if (waitingMadaCheckout && hyperPay.error) {
      setWaitingMadaCheckout(false);
      setFormError("تعذر تجهيز عملية دفع مدى، يرجى المحاولة مرة أخرى");
    }
  }, [waitingMadaCheckout, hyperPay.error]);

  if (!payingData || !payingData.cash) {
    return <Redirect to={PORTAL_LAND_PAGE_SUBSCRIBE_NOW} />;
  }

  const validateRedeemData = () => {
    if (!msisdn) {
      setFormError("يرجى إدخال رقم جوال سعودي صحيح");
      return false;
    }
    if (!normalizedRedeemAmount) {
      setFormError("يرجى إدخال مبلغ صحيح للاستبدال");
      return false;
    }
    if (normalizedRedeemAmount > totalAmount) {
      setFormError("المبلغ أكبر من الإجمالي المستحق");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleRequestOtp = () => {
    if (!validateRedeemData()) {
      return;
    }
    const reference = internalReference || `QETAF-${Date.now()}`;
    setInternalReference(reference);
    setPinDigits(["", "", "", ""]);
    setPendingRedeemAmount(0);
    setRedeemedAmount(0);
    lastRedeemPayloadRef.current = "";
    dispatch(requestQetafOtp(Number(msisdn), reference));
  };

  const handleRedeem = (enteredPin = pin) => {
    if (!validateRedeemData()) {
      return;
    }
    if (!internalReference || !qetaf.otpSuccess) {
      setFormError("يرجى إرسال رمز التحقق أولاً");
      return;
    }
    if (!/^\d{4}$/.test(enteredPin)) {
      setFormError("يرجى إدخال رمز التحقق المكون من 4 أرقام");
      return;
    }
    const redeemPayloadKey = `${msisdn}-${enteredPin}-${normalizedRedeemAmount}-${internalReference}`;
    if (qetaf.redeemLoading || lastRedeemPayloadRef.current === redeemPayloadKey) {
      return;
    }
    setFormError("");
    setPendingRedeemAmount(normalizedRedeemAmount);
    lastRedeemPayloadRef.current = redeemPayloadKey;
    const remainingAmountSar = Math.max(totalAmount - normalizedRedeemAmount, 0);
    dispatch(
      redeemQetafPoints(
        Number(msisdn),
        Number(enteredPin),
        normalizedRedeemAmount,
        internalReference,
        remainingAmountSar
      )
    );
  };

  const handlePinChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextDigits = [...pinDigits];
    nextDigits[index] = digit;
    const nextPin = nextDigits.join("");
    setPinDigits(nextDigits);
    if (digit && index < pinRefs.current.length - 1) {
      pinRefs.current[index + 1].focus();
    }
    if (/^\d{4}$/.test(nextPin)) {
      handleRedeem(nextPin);
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      e.preventDefault();
      const prev = pinRefs.current[index - 1];
      if (prev) {
        prev.focus();
      }
    }
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    setRedeemedAmount(0);
    setPendingRedeemAmount(0);
    lastRedeemPayloadRef.current = "";
  };

  const handleAmountChange = (value) => {
    const digits = value.replace(/\D/g, "");
    setRedeemAmount(digits);
    setRedeemedAmount(0);
    setPendingRedeemAmount(0);
    lastRedeemPayloadRef.current = "";
  };

  const persistQetafCheckoutData = (remainingPayMethod) => {
    const remainingAmountUsd = getRemainingAmountUsd(payingData, remainingAmount);

    dispatch(
      addPaymentDataWithCode(
        remainingAmount,
        payingData.paymentType,
        payingData.expirationDate,
        payingData.isWithOffer,
        payingData.codeName,
        payingData.paymentCurrencyPackage,
        {
          qetafInternalReference: internalReference,
          qetafRedeemedAmount: appliedQetafDiscount,
          qetafRemainingAmount: remainingAmount,
          qetafRemainingAmountUsd: remainingAmountUsd,
          qetafRemainingMethod: remainingPayMethod,
        }
      )
    );
  };

  const handleCompletePayment = () => {
    if (!redeemResponseOk) {
      setFormError("يرجى استكمال عملية استبدال النقاط أولاً");
      return;
    }
    if (!internalReference) {
      setFormError("تعذر تحديد رقم مرجعي لعملية قطاف");
      return;
    }
    setFormError("");

    if (remainingAmount <= 0) {
      dispatch(
        completeQetafSubscription(
          internalReference,
          payingData.paymentType,
          0,
          null
        )
      );
      return;
    }

    persistQetafCheckoutData(remainingBalanceMethod);
    if (remainingBalanceMethod === "mada") {
      dispatch(emptyStatusMadaId());
      setWaitingMadaCheckout(true);
      dispatch(prepareCheckout(remainingAmount, JeelMADA));
      return;
    }
    history.push(PORTAL_CREDIT_CARD);
  };

  return (
    <div className="qetaf-payment-page" dir="rtl">
      <div className="container">
        <div className="page-custom-nav d-flex align-items-center justify-content-between my-3">
          <div className="d-flex align-items-center">
            <Link className="navbar-brand d-flex align-items-center" to="/">
              <img src={mainLogo} alt="Jeel" />
            </Link>
            <div className="complete-process d-none d-sm-block">
              اكمال عملية الدفع
            </div>
          </div>
          <Link className="btn back-btn" type="button" to="/">
            العوده
          </Link>
        </div>
      </div>

      <div className="container qetaf-payment-container">
        <div className="row">
          <div className="col-12 col-lg-7">
            <div className="qetaf-payment-title text-right">
              <h3>طريقة الدفع</h3>
              <p>استبدل نقاطك واختر كيف تود دفع المبلغ المتبقي.</p>
            </div>

            <div className="qetaf-card qetaf-selected-card" dir="rtl">
              <div className="qetaf-dialog-header">
                <div className="qetaf-dialog-title-group">
                  <div className="qetaf-gift-icon">%</div>
                  <div className="qetaf-dialog-title">
                    <h5>نقاط قطاف</h5>
                  </div>
                </div>
                <img className="qetaf-logo" src={qitafLogo} alt="Qitaf" />
              </div>

              <label className="qetaf-field-label">رقم الهاتف</label>

              <div className="qetaf-phone-row" dir="ltr">
                <div className="qetaf-country-code">
                  <span className="qetaf-flag-mark"></span>
                  <span>+966</span>
                  <span className="qetaf-country-chevron" aria-hidden />
                </div>
                <input
                  className="qetaf-phone-input"
                  type="text"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="5XXXXXXXX"
                />
              </div>

              <label className="qetaf-field-label">تحديد المبلغ</label>

              <div className="qetaf-points-row" dir="ltr">
                <button
                  className="btn qetaf-secondary-btn"
                  type="button"
                  onClick={handleRequestOtp}
                  disabled={qetaf.otpLoading}
                >
                  {qetaf.otpLoading ? "جاري التحديث" : "تحديث"}
                </button>
                <div className="qetaf-points-input">
                  <span className="qetaf-points-unit">{currency}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={redeemAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="qetaf-otp-panel">
                <div className="qetaf-otp-heading" dir="ltr">
                  <button
                    className="qetaf-link-btn"
                    type="button"
                    onClick={handleRequestOtp}
                    disabled={qetaf.otpLoading}
                  >
                    إعادة إرسال الرمز
                  </button>
                  <span className="qetaf-otp-instruction">
                    أدخل الرمز المرسل إلى{" "}
                    {otpMsisdnHint ? (
                      <strong>{otpMsisdnHint}</strong>
                    ) : (
                      "رقم هاتفك"
                    )}
                  </span>
                </div>
                <div className="qetaf-pin-row" dir="ltr">
                  {pinDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        pinRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handlePinKeyDown(index, e)}
                    />
                  ))}
                </div>
                <div className="qetaf-success-strip" dir="rtl">
                  <span>
                    سيتم خصم {formatAmount(selectedQetafDiscount)} {currency} بعد التحقق
                  </span>
                  <span className="qetaf-success-check" aria-hidden>
                    ✓
                  </span>
                </div>
              </div>
              {(formError || otpMessage || redeemErrorMessage || completeErrorMessage) && (
                <div className="qetaf-error">
                  {formError || otpMessage || redeemErrorMessage || completeErrorMessage}
                </div>
              )}
              {qetaf.otpSuccess && (
                <div className="qetaf-info">تم إرسال رمز التحقق إلى رقم الجوال</div>
              )}
              {redeemResponseOk && (
                <div className="qetaf-info">
                  تم استبدال نقاط قطاف بنجاح. المبلغ المتبقي:{" "}
                  {formatAmount(remainingAmount)} {currency}
                </div>
              )}
              {qetaf.redeemSuccess && !redeemResponseOk && (
                <div className="qetaf-error">{redeemResponseMessage}</div>
              )}
            </div>
          </div>

          <div className="col-12 col-lg-5">
            <div className="qetaf-summary-card">
              <div className="qetaf-summary-package">
                <div>
                  <p>الباقة الحالية</p>
                  <h4>{getPackageType(payingData.paymentType)}</h4>
                </div>
                <div className="qetaf-summary-price">
                  {formatAmount(totalAmount)} <small>{currency}</small>
                </div>
              </div>

              <div className="qetaf-total-lines">
                <div>
                  <span>المجموع</span>
                  <strong>{formatAmount(totalAmount)} {currency}</strong>
                </div>
                <div className="qetaf-discount-line">
                  <span>خصم قطاف</span>
                  <strong>- {formatAmount(appliedQetafDiscount)} {currency}</strong>
                </div>
                <div>
                  <span>ضريبة القيمة المضافة (15%)</span>
                  <strong>مشمولة</strong>
                </div>
              </div>

              <div className="qetaf-final-total">
                <span>الإجمالي المستحق اليوم</span>
                <strong>{formatAmount(remainingAmount)} {currency}</strong>
              </div>

              {remainingAmount > 0 && (
                <>
                  <div className="qetaf-section-divider qetaf-section-divider--dashed qetaf-summary-remaining-divider">
                    <span className="qetaf-section-divider-label">
                      <svg
                        className="qetaf-divider-card-icon"
                        width="20"
                        height="16"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <rect
                          x="2"
                          y="5"
                          width="20"
                          height="14"
                          rx="2"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                      دفع الرصيد المتبقي
                    </span>
                  </div>
                  <div
                    className="qetaf-remaining-choice-grid"
                    role="radiogroup"
                    aria-label="طريقة دفع الرصيد المتبقي"
                  >
                    <label
                      className={`qetaf-remaining-choice${remainingBalanceMethod === "credit" ? " is-selected" : ""}`}
                    >
                      <input
                        className="qetaf-remaining-choice-radio"
                        type="radio"
                        name="remainingBalancePay"
                        checked={remainingBalanceMethod === "credit"}
                        onChange={() => setRemainingBalanceMethod("credit")}
                      />
                      <span className="qetaf-remaining-choice-visual">
                        <span className="qetaf-remaining-cc-badge">
                          <img src={cardsLogo} alt="" />
                        </span>
                      </span>
                      <span className="qetaf-remaining-choice-title">بطاقات ائتمان</span>
                    </label>
                    <label
                      className={`qetaf-remaining-choice${remainingBalanceMethod === "mada" ? " is-selected" : ""}`}
                    >
                      <input
                        className="qetaf-remaining-choice-radio"
                        type="radio"
                        name="remainingBalancePay"
                        checked={remainingBalanceMethod === "mada"}
                        onChange={() => setRemainingBalanceMethod("mada")}
                      />
                      <span className="qetaf-remaining-choice-visual">
                        <img className="qetaf-remaining-mada-logo" src={madaLogo} alt="مدى" />
                      </span>
                      <span className="qetaf-remaining-choice-title">بطاقات مدى</span>
                    </label>
                  </div>
                </>
              )}

              <button
                className="btn qetaf-submit-btn"
                type="button"
                onClick={handleCompletePayment}
                disabled={qetaf.redeemLoading || qetaf.completeLoading || waitingMadaCheckout || hyperPay.loading}
              >
                {qetaf.completeLoading
                  ? "جاري تفعيل الاشتراك"
                  : waitingMadaCheckout || hyperPay.loading
                    ? "جاري تجهيز الدفع"
                    : "إتمام الدفع"}
              </button>

              <div className="qetaf-safe-note">
                عملية دفع آمنة وموثوقة
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QetafPayment;
